// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The Trappe tool's drawing (frontend-u0o.10, slice S8; mock 06 — Revit's
 * stair by run): the first click is run 1's first riser; run 1 grows with
 * the pointer in whole goings ("9 af 15 trin") up to the risers the storey
 * height needs; typed digits set the riser count, or (Tab) the run length,
 * which the going stretches to fit. A click with every riser placed
 * finishes the stair; a click short of them lays a landing, and run 2 —
 * straight on, or turned to the pointer's side for an L or U (Mellemrum
 * flips the side) — takes the rest on the next click. Pure: no React, no
 * DOM.
 */

import type { XY } from './planWalls';
import { layoutStair, type LayoutInput, type StairLayout, type StairLocation } from './stairs';

export interface StairDrawContext {
  /** Risers the whole stair needs (from the storey height, or typed). */
  total: number;
  going: number;
  width: number;
  location: StairLocation;
  mode: 'straight' | 'L' | 'U' | 'spiral';
}

/** Where a drawing stands: nothing yet, run 1 from `start`, or run 2 after `first` risers along `dir`. */
export interface StairDraw {
  start: XY | null;
  dir: XY | null;
  first: number | null;
  /** The going run 1 was laid in (a typed length stretches it). */
  going: number | null;
  /** Mellemrum: run 2 to the other side than the pointer's. */
  flip: boolean;
}

export const EMPTY_DRAW: StairDraw = { start: null, dir: null, first: null, going: null, flip: false };

/** The measurement box's typed values. */
export interface StairTyped {
  risers?: number;
  length?: number;
}

export interface StairDraft {
  layout: StairLayout;
  input: LayoutInput;
  first: number;
  going: number;
  dir: XY;
}

/** Below this (metres) a click on the start places nothing: no direction yet. */
const MIN_PULL = 0.05;

const sub = (a: XY, b: XY): XY => [a[0] - b[0], a[1] - b[1]];
const add = (a: XY, b: XY): XY => [a[0] + b[0], a[1] + b[1]];
const mul = (a: XY, k: number): XY => [a[0] * k, a[1] * k];

const mode2 = (m: StairDrawContext['mode']): LayoutInput['mode'] => (m === 'L' || m === 'U' ? m : 'straight');

/** The most risers run 1 may take: all of them straight; an L or U keeps one for run 2. */
const maxFirst = (ctx: StairDrawContext) => Math.max(1, mode2(ctx.mode) === 'straight' ? ctx.total : ctx.total - 1);

/** The stair as the pointer (and anything typed) would leave it; null before a start or with no direction. */
export function draftOf(draw: StairDraw, ctx: StairDrawContext, p: XY | null, typed: StairTyped | null): StairDraft | null {
  if (!draw.start) return null;
  const base = { width: ctx.width, location: ctx.location, mode: mode2(ctx.mode) };
  if (draw.first === null || !draw.dir) {
    const v = p ? sub(p, draw.start) : ([0, 0] as XY);
    const L = Math.hypot(v[0], v[1]);
    const dir: XY | null = L > 1e-9 ? [v[0] / L, v[1] / L] : draw.dir;
    if (!dir) return null;
    const pulled = typed?.length ?? L;
    let first = typed?.risers ?? Math.round(pulled / ctx.going) + 1;
    first = Math.min(Math.max(1, Math.round(first)), maxFirst(ctx));
    const going = typed?.length !== undefined && first > 1 ? typed.length / (first - 1) : ctx.going;
    // Run 1 alone until it is placed (the rest is the next click's).
    const input: LayoutInput = { ...base, start: draw.start, dir, first, total: first, going, turn: 1 };
    return { layout: layoutStair(input), input: { ...input, total: ctx.total }, first, going, dir };
  }
  const going = draw.going ?? ctx.going;
  const end1 = add(draw.start, mul(draw.dir, (draw.first - 1) * going));
  const q = p ? sub(p, end1) : ([0, 0] as XY);
  const side = draw.dir[0] * q[1] - draw.dir[1] * q[0];
  const pointerTurn: 1 | -1 = side < 0 ? -1 : 1;
  const turn: 1 | -1 = draw.flip ? (pointerTurn === 1 ? -1 : 1) : pointerTurn;
  const input: LayoutInput = { ...base, start: draw.start, dir: draw.dir, first: draw.first, total: ctx.total, going, turn };
  return { layout: layoutStair(input), input, first: draw.first, going, dir: draw.dir };
}

/**
 * A click (or Enter) at `p`: the start; run 1 placed — the whole stair
 * when every riser is in it, else a landing and run 2 next; or run 2
 * placed. `commit` is the finished stair's layout.
 */
export function clickStair(draw: StairDraw, ctx: StairDrawContext, p: XY, typed: StairTyped | null): { draw: StairDraw; commit: StairLayout | null } {
  if (!draw.start) return { draw: { ...EMPTY_DRAW, start: p }, commit: null };
  if (draw.first === null) {
    if (!typed && Math.hypot(p[0] - draw.start[0], p[1] - draw.start[1]) < MIN_PULL) return { draw, commit: null };
    const d = draftOf(draw, ctx, p, typed);
    if (!d) return { draw, commit: null };
    if (d.first >= ctx.total) return { draw: EMPTY_DRAW, commit: layoutStair({ ...d.input, total: ctx.total, first: ctx.total }) };
    return { draw: { start: draw.start, dir: d.dir, first: d.first, going: d.going, flip: false }, commit: null };
  }
  const d = draftOf(draw, ctx, p, null);
  return d ? { draw: EMPTY_DRAW, commit: d.layout } : { draw, commit: null };
}

/** The measurement box's count: "9 af 15 trin", and how many are left. */
export function readout(draw: StairDraw, ctx: StairDrawContext, d: StairDraft | null): { count: string; left: number } {
  const placed = draw.first === null ? (d?.first ?? 0) : ctx.total;
  return { count: `${placed} af ${ctx.total} trin`, left: Math.max(0, ctx.total - placed) };
}
